import { useCallback, useEffect, useRef } from 'react';
import { usePrivy, useLogin, useLogout, User as PrivyUser } from '@privy-io/react-auth';
import { useNavigate, useLocation } from 'react-router-dom';
import { api, User } from '../lib/api';
import { useAuthStore } from '../stores/auth.store';
import { disconnectSocket } from '../lib/socket';

interface UseAuthReturn {
  // State
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  privyUser: PrivyUser | null;
  privyReady: boolean;

  // Actions
  login: () => void;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

export function useAuth(): UseAuthReturn {
  const {
    ready: privyReady,
    authenticated: privyAuthenticated,
    user: privyUser,
    getAccessToken,
  } = usePrivy();

  const navigate = useNavigate();
  const location = useLocation();

  // Use individual selectors to avoid re-render loops from full store object
  const user = useAuthStore((s) => s.user);
  const token = useAuthStore((s) => s.token);
  const refreshToken = useAuthStore((s) => s.refreshToken);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isLoading = useAuthStore((s) => s.isLoading);
  const storeLogin = useAuthStore((s) => s.login);
  const storeLogout = useAuthStore((s) => s.logout);
  const storeSetLoading = useAuthStore((s) => s.setLoading);

  // Guard against concurrent/repeated sync attempts
  const syncingRef = useRef(false);
  const syncAttemptedRef = useRef(false);

  const checkOnboarding = useCallback(async () => {
    // Don't redirect if already on onboarding
    if (location.pathname === '/onboarding') return;

    try {
      const accounts = await api.getGameAccounts();
      if (accounts.length === 0) {
        navigate('/onboarding', { replace: true });
      }
    } catch {
      // If the API call fails, don't block the user
    }
  }, [navigate, location.pathname]);

  // Sync Privy auth state with backend
  const syncWithBackend = useCallback(async () => {
    if (!privyAuthenticated) {
      storeLogout();
      api.setToken(null);
      api.setRefreshToken(null);
      return;
    }

    // Prevent concurrent or repeated syncs
    if (syncingRef.current) return;
    syncingRef.current = true;

    try {
      storeSetLoading(true);
      const accessToken = await getAccessToken();

      if (!accessToken) {
        console.error('[Auth] No access token available');
        storeSetLoading(false);
        return;
      }

      // Verify with backend and get app JWT
      const response = await api.verifyPrivy(accessToken);

      // Store the app token and refresh token
      api.setToken(response.tokens.accessToken);
      api.setRefreshToken(response.tokens.refreshToken);
      storeLogin(response.user, response.tokens.accessToken, response.tokens.refreshToken);
      syncAttemptedRef.current = true;

      // Check if user needs onboarding
      await checkOnboarding();
    } catch (error) {
      console.error('[Auth] Failed to sync with backend:', error);
      // Don't call logout on sync failure — that would clear isAuthenticated
      // and trigger the effect to re-sync, creating an infinite loop.
      // Just clear loading so the UI isn't stuck.
      storeSetLoading(false);
      syncAttemptedRef.current = true;
    } finally {
      syncingRef.current = false;
    }
  }, [privyAuthenticated, getAccessToken, storeLogin, storeLogout, storeSetLoading, checkOnboarding]);

  const { login: privyLogin } = useLogin({
    onComplete: async () => {
      syncAttemptedRef.current = false;
      await syncWithBackend();
    },
    onError: (error) => {
      console.error('[Auth] Privy login error:', error);
      storeSetLoading(false);
    },
  });

  const { logout: privyLogout } = useLogout({
    onSuccess: () => {
      storeLogout();
      api.setToken(null);
      api.setRefreshToken(null);
      disconnectSocket();
      syncAttemptedRef.current = false;
    },
  });

  // Sync on Privy auth state change
  useEffect(() => {
    if (privyReady) {
      if (privyAuthenticated && !isAuthenticated && !syncAttemptedRef.current) {
        syncWithBackend();
      } else if (!privyAuthenticated && isAuthenticated) {
        storeLogout();
        api.setToken(null);
        api.setRefreshToken(null);
      } else if (!privyAuthenticated) {
        storeSetLoading(false);
      }
    }
  }, [privyReady, privyAuthenticated, isAuthenticated, storeLogout, storeSetLoading, syncWithBackend]);

  // Wire up API client callbacks (stable — only once on mount)
  useEffect(() => {
    api.setOnLogout(() => {
      privyLogout();
    });
    api.setOnTokenRefreshed((newToken, newRefresh) => {
      // Keep Zustand in sync after a silent 401 refresh
      // so React effects don't overwrite with stale tokens
      useAuthStore.setState({ token: newToken, refreshToken: newRefresh });
    });
  }, [privyLogout]);

  // Sync API client whenever store tokens change (login, refresh, etc.)
  useEffect(() => {
    api.setToken(token);
  }, [token]);

  useEffect(() => {
    api.setRefreshToken(refreshToken);
  }, [refreshToken]);

  const login = useCallback(() => {
    privyLogin();
  }, [privyLogin]);

  const logout = useCallback(async () => {
    await privyLogout();
  }, [privyLogout]);

  const refreshUser = useCallback(async () => {
    syncAttemptedRef.current = false;
    await syncWithBackend();
  }, [syncWithBackend]);

  return {
    user,
    isAuthenticated,
    isLoading: isLoading || !privyReady,
    privyUser: privyUser || null,
    privyReady,
    login,
    logout,
    refreshUser,
  };
}
